import { Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { ArrowUturnLeftIcon, CheckCircleIcon, CheckIcon, ExclamationTriangleIcon, MinusCircleIcon, PlusCircleIcon, ShieldExclamationIcon } from '@heroicons/react/20/solid';
import Slider from "rc-slider";
import { Skill, TemplateData, TemplateSkillMapping, TemplateWithQuestionnaires, AssessmentSection, SectionSkillMapping } from '../../../Models';
import Loading from '../../Shared/Loading';
import SkillDropdown from '../Dropdown/SkillDropdown';
import { useNotification } from '../../../context/NotificationContext';
import Stepper from '../Stepper';
import { getAllFilterQuestions } from '../../../api/assessmentquestion.api';
import { fetchAllSkills } from '../../../api/skill.api';
import { SectionGenerationData } from '../../../api/config';
import useApiToken from '../../../hooks/useApiToken';
import { generateAssessmentTemplateTests, generateSectionedAssessmentTemplateTests, postAssessmentTemplate, postAssessmentTemplateTests, postTemplateSkillMapping, recalibrateTemplate, UpdateLog } from '../../../api/assessmenttemplate.api';
import { createSectionsBulk } from '../../../api/assessmentsection.api';

export interface Question {
    id: string;
    text: string;
    type: string;
    time: number;
    tags: string;
    questionGroup: string | null;
    answers: any | null;
    candidateAnswers: any | null;
    numOfMaxAttempts: number | null;
    accuracy: number;
    sectionId?: string;
    sectionOrderIndex?: number;
    orderWithinSection?: number;
}
export interface TestDetails {
    numberOfTests: string;
    baseScore: string;
    exceptionalScore: string;
    time: string | number;
    questionOption: string;  // Can be 'allQuestions' or 'byCandidateAnswers'
    numberOfTimesAnswered: string | number;
    breakAfterNoQuestions:number;
    breakTime:number;
}


export default function GenerateTemplateTestModal({
    size, onCancel, isModalClosed, getTemplateTestData, handleAddAssessmentTemplateTest, prevInputFields, getTemplateSkillMappings, isCreating, currentSelectedTemplate, prevSkillMappings, fetchTemplatesAndRoles, assignTest, isRecalibrating, isEditable
}: {
    title: string, content: string, onCancel: Function, isModalClosed: Function, size: string, getTemplateTestData: Function, handleAddAssessmentTemplateTest: Function, currentSelectedTemplate: TemplateWithQuestionnaires | undefined, prevSkillMappings: TemplateSkillMapping[], isCreating: boolean, getTemplateSkillMappings?: Function, prevInputFields?: TestDetails, fetchTemplatesAndRoles: Function, isRecalibrating: boolean, isEditable: boolean, assignTest?: {
        derivatives: boolean, test: boolean

    }
}) {
    const token = useApiToken();
    const [open, setOpen] = useState(true);
    const [pageNumber, setPageNumber] = useState<number>(0);
    const [selectedSkillMappings, setSelectedSkillMappings] = useState<TemplateSkillMapping[]>([{
        skillId: '',
        noOfQuestionsMin: 0,
        noOfQuestionsMax: 100,
        difficultyMin: 0,
        difficultyMax: 100,
        variationMin: 0,
        variationMax: 100,
        newQuestionPercentageMin: 0,
        newQuestionPercentageMax: 100,
        questionPercentage: 0,
        aboveThresholdPercentage: 0,
        belowThresholdPercentage: 0,
        locked: false
    }])

    const [templateData, setTemplateData] = useState<TemplateData>({
        name: '',
        description: '',
        time: '',
        numOfQuestions: '',
        positivePoints: '',
        negativePoints: '',
        breakAfterNoQuestions:0,
        breakTime:0
    });

    const [fetchedAssessments, setFetchedAssessments] = useState<Question[][]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const cancelButtonRef = useRef(null);
    const [allSkills, setAllSkills] = useState<Skill[]>([]);
    const [skillDetails, setSkillDetails] = useState<{ questions: []; totalCount: number; minDifficultyLevel: number, maxDifficultyLevel: number, threshold: number }[] | undefined>([]);
    const [inputFields, setInputFields] = useState<TestDetails>({
        numberOfTests: "",
        baseScore: "",
        exceptionalScore: "",
        time: currentSelectedTemplate?.time || "",
        questionOption: 'allQuestions',
        numberOfTimesAnswered: "-1",
        breakAfterNoQuestions:0,
        breakTime:0
    });

    const [isAssessmentFetched, setIsAssessmentsFetched] = useState<boolean>(false);
    const [selectedAssessments, setSelectedAssessments] = useState<boolean[]>(
        Array(fetchedAssessments.length).fill(true)
    );
    const [keepAssessmentTime, setKeepAssessmentTime] = useState<boolean>(true);
    const [isDividedEq, setIsDividedEq] = useState(false);
    const { success, error: notifyError } = useNotification();

    // Section-related state
    const [useSections, setUseSections] = useState<boolean>(false);
    const [sections, setSections] = useState<{
        id: string;
        title: string;
        orderIndex: number;
        hasBreakAfter: boolean;
        numOfQuestions: number;
        skillMappings: TemplateSkillMapping[];
        skillDetails: { questions: []; totalCount: number; minDifficultyLevel: number; maxDifficultyLevel: number; threshold: number }[];
    }[]>([]);

    // Section handlers
    const handleAddSection = () => {
        setSections(prev => [...prev, {
            id: crypto.randomUUID(),
            title: `Section ${prev.length + 1}`,
            orderIndex: prev.length,
            hasBreakAfter: true,
            numOfQuestions: 10,
            skillMappings: [{
                skillId: '',
                noOfQuestionsMin: 0,
                noOfQuestionsMax: 100,
                difficultyMin: 0,
                difficultyMax: 100,
                variationMin: 0,
                variationMax: 100,
                newQuestionPercentageMin: 0,
                newQuestionPercentageMax: 100,
                questionPercentage: 0,
                aboveThresholdPercentage: 0,
                belowThresholdPercentage: 0,
                locked: false
            }],
            skillDetails: []
        }]);
    };

    const handleRemoveSection = (sectionIndex: number) => {
        setSections(prev => prev.filter((_, i) => i !== sectionIndex).map((s, i) => ({ ...s, orderIndex: i })));
    };

    const handleSectionChange = (sectionIndex: number, field: string, value: any) => {
        setSections(prev => prev.map((s, i) => i === sectionIndex ? { ...s, [field]: value } : s));
    };

    const handleSectionSkillSelect = (sectionIndex: number, skillIndex: number, skillId: string) => {
        if (!token) return;
        getAllFilterQuestions(token, { min: 0, max: 100 }, [], [skillId], [], []).then((res) => {
            const questions = res.questions || [];
            let minAccuracy = Math.min(...questions.map((q: any) => q.accuracy));
            let maxAccuracy = Math.max(...questions.map((q: any) => q.accuracy));
            minAccuracy = minAccuracy === Infinity ? 0 : Math.floor(minAccuracy);
            maxAccuracy = maxAccuracy === -Infinity ? 100 : Math.ceil(maxAccuracy);
            let threshold = res.questions.length > 0 && res.questions[0].skills[0].threshold;

            setSections(prev => prev.map((section, si) => {
                if (si !== sectionIndex) return section;

                const newSkillDetails = [...section.skillDetails];
                newSkillDetails[skillIndex] = {
                    ...res,
                    minDifficultyLevel: Math.round(minAccuracy),
                    maxDifficultyLevel: Math.round(maxAccuracy),
                    threshold: threshold
                };

                const newSkillMappings = [...section.skillMappings];
                newSkillMappings[skillIndex] = {
                    ...newSkillMappings[skillIndex],
                    skillId: skillId,
                    noOfQuestionsMin: 1,
                    noOfQuestionsMax: questions.length,
                    difficultyMin: minAccuracy,
                    difficultyMax: maxAccuracy,
                    variationMin: 0,
                    variationMax: 100,
                    questionPercentage: 0,
                    newQuestionPercentageMin: 0,
                    newQuestionPercentageMax: 100,
                    locked: false
                };

                return { ...section, skillMappings: newSkillMappings, skillDetails: newSkillDetails };
            }));
        });
    };

    const handleAddSectionSkill = (sectionIndex: number) => {
        setSections(prev => prev.map((s, i) => {
            if (i !== sectionIndex) return s;
            return {
                ...s,
                skillMappings: [...s.skillMappings, {
                    skillId: '',
                    noOfQuestionsMin: 0,
                    noOfQuestionsMax: 100,
                    difficultyMin: 0,
                    difficultyMax: 100,
                    variationMin: 0,
                    variationMax: 100,
                    newQuestionPercentageMin: 0,
                    newQuestionPercentageMax: 100,
                    questionPercentage: 0,
                    aboveThresholdPercentage: 0,
                    belowThresholdPercentage: 0,
                    locked: false
                }]
            };
        }));
    };

    const handleRemoveSectionSkill = (sectionIndex: number, skillIndex: number) => {
        setSections(prev => prev.map((s, i) => {
            if (i !== sectionIndex) return s;
            return {
                ...s,
                skillMappings: s.skillMappings.filter((_, si) => si !== skillIndex),
                skillDetails: s.skillDetails.filter((_, si) => si !== skillIndex)
            };
        }));
    };

    const handleSectionSkillMappingChange = (sectionIndex: number, skillIndex: number, field: string, value: any) => {
        setSections(prev => prev.map((s, i) => {
            if (i !== sectionIndex) return s;
            return {
                ...s,
                skillMappings: s.skillMappings.map((sm, si) => si === skillIndex ? { ...sm, [field]: value } : sm)
            };
        }));
    };

    const handleToggleSectionSkillLock = (sectionIndex: number, skillIndex: number) => {
        setSections(prev => prev.map((s, i) => {
            if (i !== sectionIndex) return s;
            return {
                ...s,
                skillMappings: s.skillMappings.map((sm, si) => si === skillIndex ? { ...sm, locked: !sm.locked } : sm)
            };
        }));
    };

    useEffect(() => {
        if (!token) return;
        fetchAllSkills(token).then((res) => {
            setAllSkills(res);
        })
    }, [token])
    useEffect(() => {
        if (!token) return;
        if (prevSkillMappings.length > 0) {
            updatePrevSkillMappings();
        }
    }, [prevSkillMappings, token])

    const handleSkillSelect = (skillId: string, index: number) => {
        if (!token) return;
        getAllFilterQuestions(token, { min: 0, max: 100 }, [], [skillId], [], []).then((res) => {
            const currentSkillData = [...(skillDetails || [])];
            const questions = res.questions || [];
            let minAccuracy = Math.min(...questions.map((q: any) => q.accuracy));
            let maxAccuracy = Math.max(...questions.map((q: any) => q.accuracy));
            minAccuracy = minAccuracy === Infinity ? 0 : Math.floor(minAccuracy);
            maxAccuracy = maxAccuracy === -Infinity ? 100 : Math.ceil(maxAccuracy);
            let threshold = res.questions.length > 0 && res.questions[0].skills[0].threshold;
            if (currentSkillData) {
                currentSkillData[index] = {
                    ...res,
                    minDifficultyLevel: Math.round(minAccuracy),
                    maxDifficultyLevel: Math.round(maxAccuracy),
                    threshold: threshold
                };
                setSkillDetails(currentSkillData);
            }

            setSelectedSkillMappings(prevMappings => {
                const updatedMappings = [...prevMappings];
                updatedMappings[index] = {
                    ...updatedMappings[index],
                    skillId: skillId,
                    noOfQuestionsMin: 1,
                    noOfQuestionsMax: questions.length,
                    difficultyMin: minAccuracy,
                    difficultyMax: maxAccuracy,
                    variationMin: 0,
                    variationMax: 100,
                    questionPercentage: 0,
                    newQuestionPercentageMin: 0,
                    newQuestionPercentageMax: 100,
                    locked: false
                };
                return updatedMappings;
            });
        });
    };

    const updatePrevSkillMappings = () => {
        let tempSkillMappings: TemplateSkillMapping[] = [];
        const currentSkillData = [...(skillDetails || [])];
        prevSkillMappings.map(async (s: TemplateSkillMapping, i) => {
            tempSkillMappings.push({
                skillId: s.skillId,
             noOfQuestionsMin: currentSelectedTemplate ? Math.max(0, Math.floor((s.questionPercentage / 100) * currentSelectedTemplate.numOfQuestions - 5)) : 0,
                noOfQuestionsMax: currentSelectedTemplate ? Math.min(currentSelectedTemplate.numOfQuestions, Math.floor((s.questionPercentage / 100) * currentSelectedTemplate.numOfQuestions + 5))  : 0,
                difficultyMin: s.difficultyMin,
                difficultyMax: s.difficultyMax,
                variationMin: s.variationMin,
                variationMax: s.variationMax,
                questionPercentage: s.questionPercentage,
                newQuestionPercentageMin: s.newQuestionPercentageMin,
                newQuestionPercentageMax: s.newQuestionPercentageMax,
                aboveThresholdPercentage: 0,
                belowThresholdPercentage: 0,
                locked: false
            })
            if (!token) return;
            await getAllFilterQuestions(token, { min: 0, max: 100 }, [], [s.skillId], [], []).then((res) => {
                const questions = res.questions || [];
                let minAccuracy = Math.min(...questions.map((q: any) => q.accuracy));
                let maxAccuracy = Math.max(...questions.map((q: any) => q.accuracy));
                minAccuracy = minAccuracy === Infinity ? 0 : Math.round(minAccuracy);
                maxAccuracy = maxAccuracy === -Infinity ? 100 : Math.round(maxAccuracy);

                if (currentSkillData) {
                    currentSkillData[i] = {
                        ...res,
                        minDifficultyLevel: Math.round(minAccuracy),
                        maxDifficultyLevel: Math.round(maxAccuracy),
                    };
                }
            });
            setSkillDetails(currentSkillData);
        })
        setSelectedSkillMappings(tempSkillMappings)
    }

    useEffect(() => {
        if (open === false) {
            isModalClosed();
            setFetchedAssessments([])
            setIsAssessmentsFetched(false);
        };
    }, [open, isModalClosed]);
    const validateTestData = (): boolean => {
        const { numberOfTests, baseScore, numberOfTimesAnswered, time, questionOption ,breakAfterNoQuestions,breakTime} = inputFields;
        const { name, numOfQuestions, positivePoints, negativePoints, } = templateData;

        // When using sections, numOfQuestions is calculated from skill mappings (using max value)
        if (useSections) {
            const totalSectionQuestions = sections.reduce((sum, s) =>
                sum + s.skillMappings.reduce((skillSum, sm) => skillSum + (sm.noOfQuestionsMax || 0), 0), 0);
            if (!numberOfTests || !baseScore || !time || !name || name.length === 0 || totalSectionQuestions === 0 || !positivePoints || !negativePoints) {
                return false;
            }

            if (isNaN(Number(numberOfTests)) || isNaN(Number(baseScore)) || isNaN(Number(time)) || isNaN(Number(positivePoints)) || isNaN(Number(negativePoints))) {
                return false;
            }

            return true;
        }

        if (!numberOfTests || !baseScore || !time || !name || name.length === 0 || !numOfQuestions || !positivePoints || !negativePoints) {
            return false;
        }

        if (isNaN(Number(numberOfTests)) || isNaN(Number(baseScore)) || isNaN(Number(time)) || isNaN(Number(numOfQuestions)) || isNaN(Number(positivePoints)) || isNaN(Number(negativePoints))) {
            return false;
        }

        if (questionOption === "byCandidateAnswers") {
            if (!numberOfTimesAnswered || isNaN(Number(numberOfTimesAnswered))) {
                return false;
            }
        }
        return true;
    };
    useEffect(() => {
        if (prevInputFields) {
            setInputFields(prevInputFields);
        }
    }, [prevInputFields])

    const distributeQuestionsEfficiently = async (
        skills: TemplateSkillMapping[],
        numberOfQuestionsPerTest: number
    ) => {
        let minTotal = skills.reduce((sum, skill) => sum + Math.round(skill.noOfQuestionsMin), 0);
        let maxTotal = skills.reduce((sum, skill) => sum + Math.round(skill.noOfQuestionsMax), 0);

        if (numberOfQuestionsPerTest) {
            if ((numberOfQuestionsPerTest < minTotal || numberOfQuestionsPerTest > maxTotal)) {
                notifyError("Error", `The sum of all minimum range for questions must be equal to ${currentSelectedTemplate ? currentSelectedTemplate.numOfQuestions : templateData?.numOfQuestions}`);
                return;
            }

            let questionDistribution = skills.map(skill => ({
                skillId: skill.skillId,
                noOfQuestions: skill.noOfQuestionsMin,
                belowThresholdQuestions: 0,
                aboveThresholdQuestions: 0
            }));

            let remainingQuestions = numberOfQuestionsPerTest - minTotal;

            while (remainingQuestions > 0) {
                for (let i = 0; i < questionDistribution.length; i++) {
                    if (questionDistribution[i].noOfQuestions < skills[i].noOfQuestionsMax) {
                        questionDistribution[i].noOfQuestions++;
                        remainingQuestions--;
                    }
                    if (remainingQuestions === 0) break;
                }
            }

            const updatedSkills = skills.map((skill, index) => {
                const skillDistribution = questionDistribution[index];
                const totalQuestions = skillDistribution.noOfQuestions;
                const newQuestionPercentageMin = skill.newQuestionPercentageMin;
                const newQuestionPercentageMax = skill.newQuestionPercentageMax;
                const minNewQuestions = Math.ceil(totalQuestions * (newQuestionPercentageMin / 100));
                const maxNewQuestions = Math.floor(totalQuestions * (newQuestionPercentageMax / 100));
                const belowThresholdQuestions = Math.max(minNewQuestions,
                    Math.min(maxNewQuestions, Math.round(totalQuestions * (newQuestionPercentageMin / 100))));

                const aboveThresholdQuestions = totalQuestions - belowThresholdQuestions;

                return {
                    ...skill,
                    questionPercentage: (totalQuestions / numberOfQuestionsPerTest) * 100,
                    belowThresholdPercentage: (belowThresholdQuestions / totalQuestions) * 100,
                    aboveThresholdPercentage: (aboveThresholdQuestions / totalQuestions) * 100,
                    belowThresholdQuestions,
                    aboveThresholdQuestions
                };
            });

            setSelectedSkillMappings(updatedSkills);
            return updatedSkills;
        }
    };
    const handlePageCalls = async (pgNo: number) => {
        if (!token) return;
        let updatedSkillMappings;
        if (!useSections) {
            if (currentSelectedTemplate) {
                updatedSkillMappings = await distributeQuestionsEfficiently(selectedSkillMappings, currentSelectedTemplate.numOfQuestions);
            } else {
                updatedSkillMappings = await distributeQuestionsEfficiently(selectedSkillMappings, Number(templateData.numOfQuestions));
            }
        }
        if (pgNo === 0) {
            setPageNumber(1);
        }
        if (pgNo === 1 && validateFields()) {
            // Handle sectioned generation
            if (useSections && sections.length > 0) {
                const sectionData: SectionGenerationData[] = sections.map((section, index) => {
                    // Calculate total questions from skill mappings (sum of noOfQuestionsMax - right slider value)
                    const totalQuestionsInSection = section.skillMappings.reduce((sum, sm) => sum + (sm.noOfQuestionsMax || 0), 0);
                    const skillMappingsData = section.skillMappings.map(sm => {
                        const questionsForSkill = sm.noOfQuestionsMax || 0;
                        const percentage = totalQuestionsInSection > 0 ? (questionsForSkill / totalQuestionsInSection) * 100 : 0;
                        return {
                            skillId: sm.skillId,
                            questionPercentage: percentage,
                            difficultyMin: sm.difficultyMin,
                            difficultyMax: sm.difficultyMax,
                            variationMin: sm.variationMin,
                            variationMax: sm.variationMax,
                            aboveThresholdQuestions: 0,
                            belowThresholdQuestions: Math.round(questionsForSkill * (sm.newQuestionPercentageMin / 100))
                        };
                    });

                    return {
                        title: section.title,
                        orderIndex: index,
                        hasBreakAfter: section.hasBreakAfter,
                        numOfQuestions: totalQuestionsInSection,
                        skillMappings: skillMappingsData
                    };
                });

                await generateSectionedAssessmentTemplateTests(
                    inputFields.numberOfTests,
                    inputFields.time,
                    inputFields.breakTime,
                    sectionData,
                    token
                ).then((res) => {
                    setFetchedAssessments(res);
                    setIsAssessmentsFetched(true);
                    setLoading(false);
                });
            }
            // Handle flat (non-sectioned) generation
            else if (updatedSkillMappings) {
                if (currentSelectedTemplate && updatedSkillMappings) {
                    await generateAssessmentTemplateTests(currentSelectedTemplate.numOfQuestions, inputFields.numberOfTests, inputFields.numberOfTimesAnswered, updatedSkillMappings, inputFields.time,inputFields.breakAfterNoQuestions,inputFields.breakTime, token).then((res) => {

                        setFetchedAssessments(res);
                        setIsAssessmentsFetched(true);
                        setLoading(false);
                    });
                }
                else {
                    await generateAssessmentTemplateTests(templateData.numOfQuestions, inputFields.numberOfTests, inputFields.numberOfTimesAnswered, updatedSkillMappings, inputFields.time, inputFields.breakAfterNoQuestions,inputFields.breakTime, token).then((res) => {

                        setFetchedAssessments(res);
                        setIsAssessmentsFetched(true);
                        setLoading(false);
                    });
                }
            }

        }
        else if (pgNo === 2) {
            const newFetchedAssessments = fetchedAssessments.filter((_, index) => selectedAssessments[index]);

            // Handle sectioned template save
            if (useSections && sections.length > 0) {
                // Calculate total questions from sections
                const totalQuestions = sections.reduce((sum, s) =>
                    sum + s.skillMappings.reduce((skillSum, sm) => skillSum + (sm.noOfQuestionsMax || 0), 0), 0);

                // Create template data with useSections flag and calculated numOfQuestions
                const sectionedTemplateData = {
                    ...templateData,
                    numOfQuestions: totalQuestions,
                    useSections: true
                };

                await postAssessmentTemplate(sectionedTemplateData, token).then(async (res1) => {
                    const templateId = res1.id;

                    // Create sections with skill mappings
                    const sectionsToCreate = sections.map((section, index) => ({
                        title: section.title,
                        description: '',
                        orderIndex: index,
                        hasBreakAfter: section.hasBreakAfter,
                        templateId: templateId,
                        skillMappings: section.skillMappings.map(sm => ({
                            skillId: sm.skillId,
                            questionPercentage: sm.noOfQuestionsMax || 0,
                            difficultyMin: sm.difficultyMin,
                            difficultyMax: sm.difficultyMax,
                            variationMin: sm.variationMin,
                            variationMax: sm.variationMax,
                            belowThresholdQuestions: Math.round((sm.noOfQuestionsMax || 0) * (sm.newQuestionPercentageMin / 100))
                        }))
                    }));

                    await createSectionsBulk(sectionsToCreate, token).then(async (sectionRes: { id: string; orderIndex: number }[]) => {
                        // Map sectionOrderIndex to real sectionId from created sections
                        const sectionIdByOrder: { [key: number]: string } = {};
                        sectionRes.forEach((section) => {
                            sectionIdByOrder[section.orderIndex] = section.id;
                        });

                        // Update questions with real section IDs based on sectionOrderIndex
                        const updatedAssessments = newFetchedAssessments.map(testQuestions =>
                            testQuestions.map(question => ({
                                ...question,
                                sectionId: question.sectionOrderIndex !== undefined
                                    ? sectionIdByOrder[question.sectionOrderIndex]
                                    : question.sectionId
                            }))
                        );

                        await postAssessmentTemplateTests(updatedAssessments, templateId, inputFields.baseScore, inputFields.time, isDividedEq, token, inputFields.exceptionalScore);
                        success("Success", "Sectioned Set Created Successfully");
                        setTimeout(async () => {
                            fetchTemplatesAndRoles();
                            setSelectedSkillMappings([]);
                            setSections([]);
                        }, 1000);
                        setOpen(false);
                    });
                });
            }
            else if (currentSelectedTemplate && !assignTest?.derivatives && !assignTest?.test) {
                if (isEditable) {
                    await UpdateLog(currentSelectedTemplate.id, token);
                }
                await postAssessmentTemplate(templateData, token).then(async (res1) => {
                    const templateId = res1.id;

                    await postTemplateSkillMapping(templateId, selectedSkillMappings, token).then(async () => {
                        await postAssessmentTemplateTests(newFetchedAssessments, templateId, inputFields.baseScore, inputFields.time, isDividedEq, token, inputFields.exceptionalScore);
                        success("Success", "Set Created Successfully");
                        setTimeout(async () => {
                            // resetTemplateData(false);
                            fetchTemplatesAndRoles();
                            setSelectedSkillMappings([]);

                        }, 1000);
                        setOpen(false);

                    });
                });
            }
            else if (assignTest?.derivatives && !assignTest?.test) {
                postTemplateSkillMapping(currentSelectedTemplate?.id, selectedSkillMappings, token).then(() => {
                    postAssessmentTemplateTests(newFetchedAssessments, currentSelectedTemplate?.id, inputFields.baseScore, inputFields.time, isDividedEq, token, inputFields.exceptionalScore);
                    success("Success", "Set Created Successfully");
                    setTimeout(async () => {
                        // resetTemplateData(false);
                        fetchTemplatesAndRoles();
                    }, 1000);

                    setOpen(false);
                });

            }
            else if (!assignTest?.derivatives && assignTest?.test) {
                postAssessmentTemplateTests(newFetchedAssessments, currentSelectedTemplate?.id, inputFields.baseScore, inputFields.time, isDividedEq, token, inputFields.exceptionalScore);
                success("Success", "Set Created Successfully");
                setTimeout(async () => {
                    // resetTemplateData(false);
                    fetchTemplatesAndRoles();
                }, 1000);

                setOpen(false);
            }
            else {
                await postAssessmentTemplate(templateData, token).then((res1) => {
                    const templateId = res1.id;
                    postTemplateSkillMapping(templateId, selectedSkillMappings, token).then(() => {
                        postAssessmentTemplateTests(newFetchedAssessments, templateId, inputFields.baseScore, inputFields.time, isDividedEq, token, inputFields.exceptionalScore);
                        success("Success", "Set Created Successfully");
                        setTimeout(async () => {
                            // resetTemplateData(false);
                            fetchTemplatesAndRoles();
                        }, 1000);

                        setOpen(false);
                    });
                });
            }
        }
        else if (pgNo === 3) {
            if (currentSelectedTemplate) {
                await recalibrateTemplate(currentSelectedTemplate.id, templateData, fetchedAssessments, selectedSkillMappings, inputFields.baseScore, token).then(() => {
                    setTimeout(async () => {
                        fetchTemplatesAndRoles();
                        setSelectedSkillMappings([]);
                    }, 1000)
                    success("Success", "Set Recalibrated Successfully");
                    setOpen(false);
                })
            }
        }
    }
    const validateFields = () => {
        // Validate sections if using sections
        if (useSections) {
            if (sections.length === 0) {
                notifyError("Error", "Please add at least one section.");
                setLoading(false);
                return false;
            }

            for (const section of sections) {
                if (!section.title || section.title.trim() === '') {
                    notifyError("Error", "Please provide a title for all sections.");
                    setLoading(false);
                    return false;
                }

                const sectionQuestionCount = section.skillMappings.reduce((sum, sm) => sum + (sm.noOfQuestionsMax || 0), 0);
                if (sectionQuestionCount <= 0) {
                    notifyError("Error", `Each section must have at least 1 question. Please adjust skill sliders for "${section.title}".`);
                    setLoading(false);
                    return false;
                }

                if (section.skillMappings.length === 0 || !section.skillMappings.some(sm => sm.skillId)) {
                    notifyError("Error", `Please select at least one skill for section "${section.title}".`);
                    setLoading(false);
                    return false;
                }

                // Check for duplicate skills within section
                const sectionSkillIds = section.skillMappings.map(sm => sm.skillId).filter(id => id);
                const hasSectionDuplicates = sectionSkillIds.some((skillId, index) => sectionSkillIds.indexOf(skillId) !== index);
                if (hasSectionDuplicates) {
                    notifyError("Error", `Please remove duplicate skills from section "${section.title}".`);
                    setLoading(false);
                    return false;
                }
            }

            return true;
        }

        // Validate flat skill mappings (original behavior)
        const skillIds = selectedSkillMappings.map(mapping => mapping.skillId);
        const hasDuplicates = skillIds.some((skillId, index) => skillIds.indexOf(skillId) !== index);
        if (hasDuplicates) {
            notifyError("Error", "Please remove duplicate skills from the list.");
            setLoading(false)

            return false;
        }
        return true;
    };
    const handleAddAssessmentSkillMapping = () => {
        setSelectedSkillMappings((prevSkillMappings) => [
            ...prevSkillMappings,
            {
                skillId: '',
                noOfQuestionsMin: 0,
                noOfQuestionsMax: 100,
                difficultyMin: 0,
                difficultyMax: 100,
                variationMin: 0,
                variationMax: 100,
                newQuestionPercentageMin: 0,
                newQuestionPercentageMax: 100,
                questionPercentage: 0,
                aboveThresholdPercentage: 0,
                belowThresholdPercentage: 0,
                locked: false
            }
        ]);
    };
    useEffect(() => {
        setFetchedAssessments([]);
        setIsAssessmentsFetched(false);
    }, [selectedSkillMappings, inputFields, templateData.name, templateData.negativePoints, templateData.numOfQuestions, templateData.positivePoints, templateData.time])

    const handleRemoveAssessmentSkillMapping = (index: number) => {
        setSelectedSkillMappings((prevSkillMappings) =>
            prevSkillMappings.filter((_, i) => i !== index)
        );
        setSkillDetails((skillDetails) =>
            skillDetails?.filter((_, i) => i !== index)
        );
        setFetchedAssessments([])
        setIsAssessmentsFetched(false);
    };
    const handleToggleLock = (index: number) => {
        setSelectedSkillMappings((prevSkillMappings) =>
            prevSkillMappings.map((skill, i) =>
                i === index ? { ...skill, locked: !skill.locked } : skill
            )
        );
    };
    const handleTestDetails = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setInputFields(prev => ({
            ...prev,
            [name]: value === '' ? '' : Number(value)
        }));
    };

    const validateInputFields = () => {
        const { name, description, time, numOfQuestions, positivePoints, negativePoints } = templateData;

        // When using sections, numOfQuestions is calculated from sections, so skip that check
        if (useSections) {
            if (
                name.trim() === '' ||
                description.trim() === '' ||
                time === '' ||
                positivePoints === '' ||
                negativePoints === ''
            ) {
                return false;
            }
            return true;
        }

        // Non-sectioned validation
        if (
            name.trim() === '' ||
            description.trim() === '' ||
            time === '' ||
            numOfQuestions === '' ||
            positivePoints === '' ||
            negativePoints === ''
        ) {
            return false;
        }
        return true;
    };
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        // type is trimmed because the coercion below is the only thing keeping numeric fields
        // numeric: a typo'd `type="number "` makes the DOM report "text", the value is stored as
        // a string, and the API then rejects it binding to int? - which is how break time was
        // silently lost on every generated assessment set.
        setTemplateData(prev => ({
            ...prev,
            [name]: type.trim() === 'number' ? Number(value) : value
        }));
    };
    useEffect(() => {
        if (currentSelectedTemplate) {
            setInputFields({
                numberOfTests: currentSelectedTemplate.assessmentQuestionnaires.length > 0 ? String(currentSelectedTemplate.assessmentQuestionnaires.length) : "",
                baseScore: currentSelectedTemplate.assessmentQuestionnaires.length > 0 ? String(currentSelectedTemplate.assessmentQuestionnaires[0].baseScore) || "60" : "",
                // Prefilled from an existing test in the set, the same way baseScore is, so
                // regenerating does not silently blank a value the set already had.
                exceptionalScore: currentSelectedTemplate.assessmentQuestionnaires.length > 0 && currentSelectedTemplate.assessmentQuestionnaires[0].exceptionalScore != null
                    ? String(currentSelectedTemplate.assessmentQuestionnaires[0].exceptionalScore)
                    : "",
                time: currentSelectedTemplate?.time || "",
                questionOption: 'allQuestions',
                numberOfTimesAnswered: "-1",
                breakAfterNoQuestions:currentSelectedTemplate.breakAfterNoQuestions,
                breakTime:currentSelectedTemplate.breakTime
            })
            setTemplateData({
                name: currentSelectedTemplate.name,
                description: currentSelectedTemplate.description,
                time: currentSelectedTemplate.time,
                numOfQuestions: currentSelectedTemplate.numOfQuestions,
                positivePoints: currentSelectedTemplate.positivePoints,
                negativePoints: currentSelectedTemplate.negativePoints,
                breakAfterNoQuestions:currentSelectedTemplate.breakAfterNoQuestions,
                breakTime:currentSelectedTemplate.breakTime
            })
        }
    }, [currentSelectedTemplate])


    const handleCheckboxChange = (index: number, checked: boolean) => {
        const newSelected = [...selectedAssessments];
        newSelected[index] = checked;
        setSelectedAssessments(newSelected);
    };

    const getAverageAccuracy = (questions: Question[]): number => {
        return questions.reduce((sum, q) => sum + q.accuracy, 0) / questions.length;
    };

    const getTotalTimeInMinutes = (questions: Question[]): number => {
        const totalSeconds = questions.reduce((sum, q) => sum + q.time, 0);
        return Math.round((totalSeconds / 60) * 10) / 10;
    };

    useEffect(() => {
        if (isDividedEq) {
            setKeepAssessmentTime(false);
        } else {
            setKeepAssessmentTime(true);
        }
    }, [isDividedEq]);

    useEffect(() => {
        if (keepAssessmentTime) {
            setIsDividedEq(false);
        } else {
            setIsDividedEq(true);
        }
    }, [keepAssessmentTime]);

    return (
        <Transition.Root show={open} as={Fragment} >
            <Dialog
                as="div"
                className="relative z-50 dark:bg-entntdarkblue"
                initialFocus={cancelButtonRef}
                onClose={() => {
                    onCancel();
                    setOpen(false);
                }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-400  bg-opacity-50 dark:bg-opacity-20 transition-opacity " />
                </Transition.Child>

                <div className="fixed inset-0 z-50 w-screen overflow-y-auto ">
                    <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0 ">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel
                                className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:w-[95%] h-[90vh] max-h-[44rem] flex flex-col dark:bg-darkbg sm:max-w-6xl"

                            >
                                <div className='bg-gray-200 h-12 p-4  dark:bg-darkbglight'>
                                    <h2 className='font-bold text-gray-600 text-sm text-left dark:text-gray-300 flex flex-row justify-between items-center'>
                                        <div>
                                            {isRecalibrating ? "Update Parameters" : "Create Template Tests"}
                                        </div>
                                        {/* <p className='italic text-xs px-4 dark:text-gray-300 font-normal'>
                                            Fill in the fields below to generate tests based on the specified values.
                                        </p> */}
                                    </h2>
                                </div>
                                <div className='flex flex-col flex-grow overflow-hidden'>
                                    {(!currentSelectedTemplate) && <Stepper currentStep={pageNumber + 1} />}
                                    <div className='flex-grow overflow-y-auto '>
                                        {!loading && pageNumber === 1 ? <>
                                            <div className='flex flex-col p-2 flex-grow gap-4 h-full dark:text-white text-[14px]'>
                                                <hr className="border-gray-300 dark:border-gray-800 " />
                                                <div className="flex flex-row items-start p-2 gap-4 mt-2 w-[90%] h-full">
                                                    <label htmlFor="" className=" text-left w-[8%]">
                                                        <small>Description:</small>
                                                        <span className='text-red-700 text-sm'>*</span>
                                                    </label>
                                                    <textarea name="description" id="" className={`block w-[90%] h-full border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-600 dark:border-gray-600 `}
                                                        onChange={handleInputChange} value={templateData.description}
                                                        placeholder='e.g Welcome to our online assessment designed to evaluate your skills for this job role. Specific skills tested are JavaScript, React, SQL and general programming concepts. The test consists of 50 multiple-choice questions and is designed to be completed within 40 minutes. This is a timed test, so please ensure you have a stable internet connection and a quiet environment to complete it.'
                                                    />
                                                </div>
                                                <div className='mt-auto p-2'>
                                                    <hr className='pb-2 border-gray-400' />
                                                    <div className='flex justify-between items-center'>
                                                        <div className='flex flex-row items-center gap-2'>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    onCancel();
                                                                    setOpen(false);
                                                                }}
                                                                className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto h-9"
                                                                ref={cancelButtonRef}
                                                            >
                                                                Cancel
                                                            </button>
                                                            {!currentSelectedTemplate && <button
                                                                type="button"
                                                                onClick={() => {
                                                                    setPageNumber(2);
                                                                }}
                                                                className="mt-3  w-full justify-center rounded-md bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm font-semibold shadow-sm  ring-gray-300 sm:mt-0 sm:w-auto h-9 flex flex-row items-center gap-2 text-white"
                                                                ref={cancelButtonRef}
                                                            >
                                                                Prev
                                                                <ArrowUturnLeftIcon height={15} />
                                                            </button>}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                // onCancel();
                                                                // setOpen(false);
                                                                // handleAddAssessmentTemplateTest();
                                                                // setInputFields(prev => ({
                                                                //     ...prev,
                                                                //     time: templateData.time,
                                                                // }));
                                                                // setPageNumber(1)
                                                                handlePageCalls(2);
                                                                // handlePageCalls(0);
                                                            }}
                                                            disabled={!validateInputFields()}
                                                            className={`rounded-md px-4 h-9 py-2 text-sm font-semibold shadow-sm focus:outline-none text-white cursor-pointer 
                                                                ${validateInputFields() ? ' bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-600 hover:bg-gray-500'}`}
                                                            ref={cancelButtonRef}
                                                        >
                                                            Save
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>

                                        </>
                                            : pageNumber === 0 ?
                                                <div className='flex flex-col h-full p-2'>
                                                    {!isRecalibrating && <>
                                                        <div className='flex flex-row flex-nowrap items-center justify-between w-full'>
                                                            <div className="flex flex-row items-center p-2 gap-2 text-xs dark:text-gray-300 shrink-0">
                                                                <label htmlFor="" className="text-left whitespace-nowrap"><small>Name: <span className='text-red-700 text-sm'>*</span> </small></label>
                                                                <input type="text" name="name" id="" className="block h-8 w-44 border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " onChange={handleInputChange}
                                                                    value={templateData.name}
                                                                    placeholder='e.g Software Eg'
                                                                />
                                                            </div>

                                                            <div className="flex flex-row items-center p-2 gap-2 text-xs dark:text-gray-300 shrink-0">
                                                                <label htmlFor="" className="text-left whitespace-nowrap"><small>Use Sections: </small></label>
                                                                <input
                                                                    type="checkbox"
                                                                    className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                                    checked={useSections}
                                                                    onChange={(e) => {
                                                                        setUseSections(e.target.checked);
                                                                        if (e.target.checked && sections.length === 0) {
                                                                            handleAddSection();
                                                                        }
                                                                    }}
                                                                />
                                                            </div>

                                                            {!useSections && (
                                                                <div className="flex flex-row items-center p-2 gap-2 text-xs dark:text-gray-300 shrink-0">
                                                                    <label htmlFor="" className="text-left whitespace-nowrap"><small>No. of Questions: <span className='text-red-700 text-sm'>*</span> </small></label>
                                                                    <input type="number" name="numOfQuestions" id="" className="block h-8 w-28 border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " onChange={handleInputChange}
                                                                        value={templateData.numOfQuestions}
                                                                        placeholder='e.g 50'
                                                                    />
                                                                </div>
                                                            )}

                                                            <div className="flex flex-row items-center p-2 gap-2 text-xs dark:text-gray-300 shrink-0">
                                                                <label htmlFor="" className="whitespace-nowrap"><small>+ve Points: <span className='text-red-700 text-sm'>*</span> </small></label>
                                                                <input type="number" name="positivePoints" id="" className="block h-8 w-20 border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600" onChange={handleInputChange}
                                                                    value={templateData.positivePoints}
                                                                    placeholder='e.g 1'
                                                                />
                                                            </div>

                                                            <div className="flex flex-row items-center p-2 gap-2 text-xs dark:text-gray-300 shrink-0">
                                                                <label htmlFor="" className="whitespace-nowrap"><small>-ve Points: <span className='text-red-700 text-sm'>*</span> </small></label>
                                                                <input type="number" name="negativePoints" id="" className="block h-8 w-20 border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600" onChange={handleInputChange}
                                                                    value={templateData.negativePoints}
                                                                    placeholder='e.g 0.25'
                                                                />
                                                            </div>

                                                        </div>
                                                        <hr className="border-gray-300 dark:border-gray-800 " />
                                                        <div className='flex flex-row'>
                                                            <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300">
                                                                <label htmlFor="" className="w-1/4 text-left"><small>Number Of Tests: <span className='text-red-700 text-sm'>*</span> </small></label>
                                                                <input type="number" name="numberOfTests" id="" className="block h-8 w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " placeholder='Eg. 3' onChange={handleTestDetails}
                                                                    value={inputFields.numberOfTests}
                                                                />
                                                            </div>
                                                            <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300">
                                                                <label htmlFor="" className="w-1/4 text-left"><small>Base Score: <span className='text-red-700 text-sm'>*</span></small></label>
                                                                <input type="number" name="baseScore" id="" className="block h-8  w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " placeholder='Eg. 60' onChange={handleTestDetails} value={inputFields.baseScore} />
                                                            </div>

                                                            <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300">
                                                                <label htmlFor="" className="w-1/4 text-left"><small>Exceptional Score: </small></label>
                                                                <input type="number" name="exceptionalScore" id="" className="block h-8  w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " placeholder='Eg. 80' onChange={handleTestDetails} value={inputFields.exceptionalScore} />
                                                            </div>

                                                            <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300 ">
                                                                <label htmlFor="" className="w-1/4 text-left"><small>Time: <small className='text-[10px]'>(in mins)</small> <span className='text-red-700 text-sm'>*</span></small></label>
                                                                <input type="number" name="time" id="" className="block h-8  w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400  dark:placeholder:text-gray-500 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:border-gray-600 " placeholder='Eg. 60 (mins)' onChange={(e) => { handleTestDetails(e); handleInputChange(e) }} value={inputFields.time} />
                                                            </div>

                                                            {!useSections && (
                                                                <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300">
                                                                    <label htmlFor="" className="w-1/4 text-left"><small>Break after no of Questions: </small></label>
                                                                    <input type="number" name="breakAfterNoQuestions" id="" className="block h-8 w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " placeholder='Eg. 3' onChange={(e)=>{handleTestDetails(e)
                                                                        handleInputChange(e);
                                                                    }}
                                                                        value={inputFields.breakAfterNoQuestions}
                                                                    />
                                                                </div>
                                                            )}

                                                            <div className="flex flex-row items-center p-2 gap-4  w-full max-w-lg text-xs dark:text-gray-300">
                                                                <label htmlFor="" className="w-1/4 text-left"><small>Break Time in mins: </small></label>
                                                                <input type="number" name="breakTime" id="" className="block h-8 w-[60%] border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs  focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 " placeholder='Eg. 3' onChange={(e)=>{
                                                                handleTestDetails(e)
                                                                handleInputChange(e)

                                                                }}
                                                                    value={inputFields.breakTime}
                                                                />
                                                            </div>

                                                        </div>
                                                        <hr className="border-gray-300 dark:border-gray-800 m-2" />
                                                    </>}

                                                    {/* <div className='flex flex-row dark:text-gray-300 text-gray-900 text-xs p-2 h-10'>
                                                    <div className='flex flex-row items-center gap-4 text-xs dark:text-gray-300'>
                                                        <div>Include Questions :</div>
                                                        <div className='flex flex-row gap-2 items-center'>
                                                            <input className='form-checkbox z-[11] bg-transparent left-4 top-1/2 rounded border-gray-300 text-indigo-600 focus:outline-none' type="radio" id="allQuestions" name="questionOption" value="allQuestions" checked={inputFields.questionOption === 'allQuestions'} onChange={handleQuestionOptionChange} />
                                                            <label htmlFor="allQuestions">All Questions</label>
                                                        </div>
                                                        <div className='flex flex-row gap-2  items-center justify-between'>
                                                            <input className='form-checkbox z-[11] bg-transparent left-4 top-1/2 rounded border-gray-300 text-indigo-600 focus:outline-none' type="radio" id="byCandidateAnswers" name="questionOption" value="byCandidateAnswers" checked={inputFields.questionOption === 'byCandidateAnswers'} onChange={handleQuestionOptionChange} />
                                                            <label htmlFor="byCandidateAnswers">By Candidate Answers: </label>
                                                            {inputFields.questionOption === 'byCandidateAnswers' && <input type="number" name="byCandidateAnswers" id="" className="block h-8  border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400  dark:placeholder:text-gray-500 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 
                                                                dark:border-gray-600 " placeholder='Eg. 300'
                                                                onChange={handleCandidateAnswersChange} value={inputFields.numberOfTimesAnswered}
                                                            />}

                                                        </div>
                                                    </div>
                                                </div> */}

                                                    <div className='px-2 dark:border-gray-700 border-gray-300 dark:text-white flex-grow overflow-hidden'>
                                                        <div className='text-xs pb-2'>{isRecalibrating ? "Update Skills:" : useSections ? "Configure Sections:" : "Select Skills:"}</div>
                                                        <div className='h-full overflow-y-auto pb-4'>
                                                            {useSections ? (
                                                                // Section-based configuration UI
                                                                <div className='flex flex-col gap-4 pb-4'>
                                                                    {sections.map((section, sectionIndex) => (
                                                                        <div key={section.id} className='border border-gray-300 dark:border-gray-600 rounded-lg p-3'>
                                                                            <div className='flex flex-row items-center justify-between mb-3'>
                                                                                <div className='flex flex-row items-center gap-4'>
                                                                                    <input
                                                                                        type="text"
                                                                                        value={section.title}
                                                                                        onChange={(e) => handleSectionChange(sectionIndex, 'title', e.target.value)}
                                                                                        className="block h-8 w-48 border border-gray-300 rounded-md bg-transparent py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 font-semibold"
                                                                                        placeholder="Section Title"
                                                                                    />
                                                                                    <div className='flex flex-row items-center gap-2 text-xs'>
                                                                                        <label>Questions:</label>
                                                                                        <input
                                                                                            type="number"
                                                                                            value={section.skillMappings.reduce((sum, sm) => sum + (sm.noOfQuestionsMax || 0), 0)}
                                                                                            readOnly
                                                                                            className="block h-8 w-20 border border-gray-300 rounded-md bg-gray-100 dark:bg-gray-700 py-1.5 pl-3 text-gray-900 placeholder:text-gray-400 placeholder:text-xs focus:ring-0 sm:text-sm sm:leading-6 dark:text-gray-300 dark:placeholder:text-gray-500 dark:border-gray-600 cursor-not-allowed"
                                                                                        />
                                                                                    </div>
                                                                                    <div className='flex flex-row items-center gap-2 text-xs'>
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={section.hasBreakAfter}
                                                                                            onChange={(e) => handleSectionChange(sectionIndex, 'hasBreakAfter', e.target.checked)}
                                                                                            className="form-checkbox h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                                                        />
                                                                                        <label>Break After</label>
                                                                                    </div>
                                                                                </div>
                                                                                <MinusCircleIcon color='red' height={20} onClick={() => handleRemoveSection(sectionIndex)} cursor={"pointer"} />
                                                                            </div>

                                                                            <div className='text-[10px] text-gray-500 dark:text-gray-400 mb-2'>Skills for this section:</div>
                                                                            {section.skillMappings.map((sm, skillIndex) => (
                                                                                <div className='flex flex-row items-center gap-12 pb-4' key={skillIndex}>
                                                                                    <div className=''>
                                                                                        <SkillDropdown
                                                                                            skills={allSkills}
                                                                                            onSkillSelect={(skillId: string) => handleSectionSkillSelect(sectionIndex, skillIndex, skillId)}
                                                                                            index={skillIndex}
                                                                                            skillIds={section.skillMappings.map(e => e.skillId)}
                                                                                        />
                                                                                        <div className="text-[10px] text-gray-500 dark:text-gray-300 italic p-2">
                                                                                            {section.skillDetails?.[skillIndex]?.totalCount !== undefined ? (
                                                                                                <>There are {section.skillDetails[skillIndex].totalCount} questions available.</>
                                                                                            ) : ""}
                                                                                        </div>
                                                                                    </div>
                                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                                        <Slider
                                                                                            range
                                                                                            max={section.skillDetails?.[skillIndex]?.totalCount ?? 100}
                                                                                            min={1}
                                                                                            value={[sm.noOfQuestionsMin || 0, sm.noOfQuestionsMax || (section.skillDetails?.[skillIndex]?.totalCount ?? 100)]}
                                                                                            onChange={(value: any) => {
                                                                                                const [min, max] = value as number[];
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'noOfQuestionsMin', min);
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'noOfQuestionsMax', max);
                                                                                            }}
                                                                                            disabled={sm.locked}
                                                                                            styles={{
                                                                                                track: { background: "#0D42EC", fontSize: "10px" },
                                                                                                handle: { borderColor: "#0D42EC", fontSize: "10px" }
                                                                                            }}
                                                                                        />
                                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>No. of Questions</div>
                                                                                    </div>
                                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                                        <Slider
                                                                                            range
                                                                                            max={section.skillDetails?.[skillIndex]?.maxDifficultyLevel ?? 100}
                                                                                            min={section.skillDetails?.[skillIndex]?.minDifficultyLevel ?? 0}
                                                                                            value={[sm.difficultyMin, sm.difficultyMax]}
                                                                                            onChange={(value: any) => {
                                                                                                const [min, max] = value as number[];
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'difficultyMin', min);
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'difficultyMax', max);
                                                                                            }}
                                                                                            disabled={sm.locked}
                                                                                            styles={{
                                                                                                track: { background: "#0D42EC", fontSize: "10px" },
                                                                                                handle: { borderColor: "#0D42EC", fontSize: "10px" }
                                                                                            }}
                                                                                        />
                                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>Difficulty</div>
                                                                                    </div>
                                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                                        <Slider
                                                                                            range
                                                                                            max={100}
                                                                                            min={0}
                                                                                            value={[sm.variationMin, sm.variationMax]}
                                                                                            onChange={(value: any) => {
                                                                                                const [min, max] = value as number[];
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'variationMin', min);
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'variationMax', max);
                                                                                            }}
                                                                                            disabled={sm.locked}
                                                                                            styles={{
                                                                                                track: { background: "#0D42EC", fontSize: "10px" },
                                                                                                handle: { borderColor: "#0D42EC", fontSize: "10px" }
                                                                                            }}
                                                                                        />
                                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>Variation</div>
                                                                                    </div>
                                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                                        <Slider
                                                                                            range
                                                                                            max={100}
                                                                                            min={0}
                                                                                            value={[sm.newQuestionPercentageMin, sm.newQuestionPercentageMax]}
                                                                                            onChange={(value: any) => {
                                                                                                const [min, max] = value as number[];
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'newQuestionPercentageMin', min);
                                                                                                handleSectionSkillMappingChange(sectionIndex, skillIndex, 'newQuestionPercentageMax', max);
                                                                                            }}
                                                                                            disabled={sm.locked}
                                                                                            styles={{
                                                                                                track: { background: "#0D42EC", fontSize: "10px" },
                                                                                                handle: { borderColor: "#0D42EC", fontSize: "10px" }
                                                                                            }}
                                                                                        />
                                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>Candidate Answer Threshold</div>
                                                                                    </div>
                                                                                    <div className='flex flex-row gap-6 items-center pb-4'>
                                                                                        <button
                                                                                            className='rounded-md px-2 text-xs py-2 font-semibold shadow-sm focus:outline-none bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer'
                                                                                            onClick={() => handleToggleSectionSkillLock(sectionIndex, skillIndex)}
                                                                                        >
                                                                                            {sm.locked ? "Unlock" : "Lock"}
                                                                                        </button>
                                                                                        <MinusCircleIcon color='red' height={20} onClick={() => handleRemoveSectionSkill(sectionIndex, skillIndex)} cursor={"pointer"} />
                                                                                    </div>
                                                                                </div>
                                                                            ))}
                                                                            <PlusCircleIcon height={18} className='ml-4 mt-1' color='blue' onClick={() => handleAddSectionSkill(sectionIndex)} cursor={"pointer"} />
                                                                        </div>
                                                                    ))}
                                                                    <div className='flex flex-row items-center gap-2 text-xs text-indigo-600 cursor-pointer hover:text-indigo-500' onClick={handleAddSection}>
                                                                        <PlusCircleIcon height={20} />
                                                                        <span>Add Section</span>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                            // Original flat skill mapping UI
                                                            <>
                                                            {selectedSkillMappings.map((s: TemplateSkillMapping, index: number) => {
                                                                return <div className='flex flex-row items-center gap-12 pb-4' key={index}>
                                                                    <div className=''>
                                                                        <SkillDropdown skills={allSkills} onSkillSelect={handleSkillSelect} index={index} skillIds={selectedSkillMappings.map((e => e.skillId))} />

                                                                        <div className="text-[10px] text-gray-500 dark:text-gray-300 italic p-2">
                                                                            {skillDetails && skillDetails[index]?.totalCount !== undefined ? (
                                                                                <>

                                                                                    There are {skillDetails[index]?.totalCount} questions available.
                                                                                </>
                                                                            ) : ""}
                                                                        </div>

                                                                    </div>
                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                        <Slider
                                                                            range
                                                                            max={skillDetails?.[index]?.totalCount ?? 100}
                                                                            min={1}
                                                                            value={[
                                                                                s.noOfQuestionsMin || 0,
                                                                                s.noOfQuestionsMax || (skillDetails?.[index]?.totalCount ?? 100)
                                                                            ]}
                                                                            onChange={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                const totalCount = skillDetails?.[index]?.totalCount ?? 100;
                                                                                if (min >= 0 && max <= totalCount) {
                                                                                    setSelectedSkillMappings(prevMappings =>
                                                                                        prevMappings.map((mapping, i) =>
                                                                                            i === index
                                                                                                ? { ...mapping, noOfQuestionsMin: min, noOfQuestionsMax: max }
                                                                                                : mapping
                                                                                        )
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onChangeComplete={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                const totalCount = skillDetails?.[index]?.totalCount ?? 100;
                                                                                if (min >= 0 && max <= totalCount) {
                                                                                }
                                                                            }}
                                                                            disabled={s.locked}
                                                                            styles={{
                                                                                track: {
                                                                                    background: "#0D42EC",
                                                                                    fontSize: "10px",
                                                                                },
                                                                                handle: {
                                                                                    borderColor: "#0D42EC",
                                                                                    fontSize: "10px",
                                                                                },
                                                                            }}
                                                                        />
                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>
                                                                            No. of Questions
                                                                        </div>
                                                                    </div><div className='w-[17%] flex flex-col items-center'>
                                                                        <Slider
                                                                            range
                                                                            max={skillDetails ? skillDetails[index]?.maxDifficultyLevel : 100}
                                                                            min={skillDetails ? skillDetails[index]?.minDifficultyLevel : 0}
                                                                            value={[s.difficultyMin, s.difficultyMax]}
                                                                            onChange={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {
                                                                                    setSelectedSkillMappings(prevMappings =>
                                                                                        prevMappings.map((mapping, i) =>
                                                                                            i === index
                                                                                                ? { ...mapping, difficultyMin: min, difficultyMax: max }
                                                                                                : mapping
                                                                                        )
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onChangeComplete={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {

                                                                                }
                                                                            }}
                                                                            disabled={s.locked}
                                                                            styles={{
                                                                                track: {
                                                                                    background: "#0D42EC",
                                                                                    fontSize: "10px"
                                                                                },
                                                                                handle: {
                                                                                    borderColor: "#0D42EC",
                                                                                    fontSize: "10px"
                                                                                },
                                                                            }}

                                                                        />
                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>
                                                                            Difficulty
                                                                        </div>
                                                                    </div>
                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                        <Slider
                                                                            range
                                                                            max={100}
                                                                            min={0}
                                                                            value={[s.variationMin, s.variationMax]}
                                                                            onChange={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {
                                                                                    setSelectedSkillMappings(prevMappings =>
                                                                                        prevMappings.map((mapping, i) =>
                                                                                            i === index
                                                                                                ? { ...mapping, variationMin: min, variationMax: max }
                                                                                                : mapping
                                                                                        )
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onChangeComplete={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {
                                                                                    // getAccuracy({
                                                                                    //     min: min,
                                                                                    //     max: max,
                                                                                    // });
                                                                                }
                                                                            }}
                                                                            disabled={s.locked}
                                                                            styles={{
                                                                                track: {
                                                                                    background: "#0D42EC",
                                                                                    fontSize: "10px"

                                                                                },
                                                                                handle: {
                                                                                    borderColor: "#0D42EC",
                                                                                    fontSize: "10px"
                                                                                },
                                                                            }}

                                                                        />
                                                                        <div className='text-[10px] text-gray-500 dark:text-gray-300'>
                                                                            Variation
                                                                        </div>
                                                                    </div>
                                                                    <div className='w-[17%] flex flex-col items-center'>
                                                                        <Slider
                                                                            range
                                                                            max={100}
                                                                            min={0}
                                                                            value={[s.newQuestionPercentageMin, s.newQuestionPercentageMax]}
                                                                            onChange={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {
                                                                                    setSelectedSkillMappings(prevMappings =>
                                                                                        prevMappings.map((mapping, i) =>
                                                                                            i === index
                                                                                                ? { ...mapping, newQuestionPercentageMin: min, newQuestionPercentageMax: max }
                                                                                                : mapping
                                                                                        )
                                                                                    );
                                                                                }
                                                                            }}
                                                                            onChangeComplete={(value: any) => {
                                                                                const [min, max] = value as number[];
                                                                                if (min >= 0 && max <= 100) {
                                                                                    // getAccuracy({
                                                                                    //     min: min,
                                                                                    //     max: max,
                                                                                    // });
                                                                                }
                                                                            }}
                                                                            disabled={s.locked}
                                                                            styles={{
                                                                                track: {
                                                                                    background: "#0D42EC",
                                                                                    fontSize: "10px"

                                                                                },
                                                                                handle: {
                                                                                    borderColor: "#0D42EC",
                                                                                    fontSize: "10px"
                                                                                },
                                                                            }}

                                                                        />

                                                                        <div className="text-[10px] text-gray-500 dark:text-gray-300">
                                                                            {skillDetails && skillDetails[index]?.totalCount !== undefined ? (
                                                                                <>

                                                                                    % Of New Questions below {skillDetails[index]?.threshold}
                                                                                </>
                                                                            ) : "Candidate Answer Threshold"}
                                                                        </div>
                                                                    </div>
                                                                    <div className='flex flex-row gap-6 items-center pb-4'>
                                                                        <button
                                                                            className='rounded-md px-2 text-xs py-2 font-semibold shadow-sm focus:outline-none bg-indigo-600 hover:bg-indigo-500 text-white cursor-pointer'
                                                                            onClick={() => handleToggleLock(index)}
                                                                        >
                                                                            {s.locked ? "Unlock" : "Lock"}
                                                                        </button>
                                                                        <MinusCircleIcon color='red' height={20} onClick={() => handleRemoveAssessmentSkillMapping(index)} cursor={"pointer"} />
                                                                    </div>
                                                                </div>
                                                            })}
                                                            <PlusCircleIcon height={20} className='m-1 mt-2 ' color='blue' onClick={handleAddAssessmentSkillMapping} cursor={"pointer"} />
                                                            </>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className='mt-auto p-2'>
                                                        <div className='flex flex-row justify-between text-gray-800 p-2 dark:border-gray-500 border-gray-300 border-y-2 text-xs mb-2 dark:text-white'>
                                                            {isAssessmentFetched ?
                                                                `${fetchedAssessments ? fetchedAssessments.length : 0}/${inputFields.numberOfTests} tests are generated with the specified values.` :
                                                                "Fill in the fields to generate tests based on the specified values."}
                                                        </div>
                                                        <div className='flex justify-between items-center'>
                                                            <div className='flex flex-row gap-2'>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        onCancel();
                                                                        setOpen(false);
                                                                    }}
                                                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto h-9"
                                                                    ref={cancelButtonRef}
                                                                >
                                                                    Cancel
                                                                </button>
                                                            </div>
                                                            {fetchedAssessments?.length === 0 ? <button
                                                                type="button"
                                                                onClick={() => {
                                                                    // onCancel();
                                                                    // setOpen(false);
                                                                    // handleAddAssessmentTemplateTest();
                                                                    // setPageNumber(2)
                                                                    handlePageCalls(1);

                                                                }}
                                                                disabled={!validateTestData()}
                                                                className={`rounded-md px-4 h-9 py-2 text-sm font-semibold shadow-sm focus:outline-none text-white cursor-pointer 
                                                                ${validateTestData() ? ' bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-600 hover:bg-gray-500'}`}
                                                                ref={cancelButtonRef}
                                                            >
                                                                Generate
                                                            </button> : <button
                                                                disabled={!validateTestData()}
                                                                className={`rounded-md px-4 h-9 py-2 text-sm font-semibold shadow-sm focus:outline-none text-white cursor-pointer 
                                                                ${validateTestData() && (isAssessmentFetched && fetchedAssessments && fetchedAssessments.length > 0) ? ' bg-indigo-600 hover:bg-indigo-500' : 'bg-gray-600 hover:bg-gray-500'}`}
                                                                ref={cancelButtonRef}
                                                                onClick={() => {
                                                                    if (isAssessmentFetched && fetchedAssessments && fetchedAssessments.length > 0) {
                                                                        if (isRecalibrating) {
                                                                            handlePageCalls(3);
                                                                        }
                                                                        else {
                                                                            // setPageNumber(1);
                                                                            setPageNumber(2);
                                                                        }
                                                                    }
                                                                }}
                                                            >
                                                                {isRecalibrating ? "Recalibrate" : "Next"}
                                                            </button>
                                                            }
                                                        </div>
                                                    </div>
                                                </div> : <div className="flex flex-col h-full">
                                                    <div className="flex flex-row justify-between text-gray-800 p-2 dark:border-gray-500 border-gray-300 border-y-2 text-xs mb-2 dark:text-white">
                                                        {isAssessmentFetched
                                                            ? `${fetchedAssessments ? fetchedAssessments.length : 0}/${inputFields.numberOfTests} tests are generated with the specified values.`
                                                            : "Fill in the fields to generate tests based on the specified values."}
                                                    </div>
                                                    <div className="p-2 dark:text-gray-300 text-gray-700 text-xs flex flex-row justify-between font-bold items-center">
                                                        <div >
                                                            Check the Assessments you wish to save

                                                        </div>
                                                        <div className='flex flex-col  gap-2'>
                                                            <div className='flex flex-row items-center gap-1'>
                                                                <input type="checkbox" id='assessmentTime' className='form-checkbox left-4 top-1/2 mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ' onChange={(e) => setKeepAssessmentTime(e.target.checked)} checked={keepAssessmentTime} />
                                                                <label htmlFor="assessmentTime" > Set Tests Time as Generated Time  </label>
                                                            </div>
                                                            <div className='flex flex-row items-center gap-1'>
                                                                <input type="checkbox" id='assessmentTime' className='form-checkbox left-4 top-1/2 mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600 ' onChange={(e) => setIsDividedEq(e.target.checked)} checked={isDividedEq} />
                                                                <label htmlFor="assessmentTime" > Divide Questions Time Equally By {inputFields.time} mins </label>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="flex-grow overflow-y-auto p-2">
                                                        {fetchedAssessments.length > 0 && (
                                                            <div className="flex flex-col h-full p-2 mt-6 gap-2">
                                                                {fetchedAssessments.map((questions, index) => {
                                                                    const assessmentName = `Generated Test ${index + 1}`;
                                                                    const averageAccuracy = getAverageAccuracy(questions);
                                                                    const totalTimeInMinutes = getTotalTimeInMinutes(questions);

                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            className="border rounded-lg border-green-800 overflow-hidden text-gray-700 dark:text-gray-300"
                                                                        >
                                                                            <div
                                                                                className="flex items-center justify-between p-4 cursor-pointer"
                                                                            >
                                                                                <div className="flex items-center">
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={selectedAssessments[index]}
                                                                                        onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                                                                                        className="form-checkbox left-4 top-1/2 mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                                                        onClick={(e) => e.stopPropagation()}
                                                                                    />
                                                                                    <span className="font-medium">{assessmentName}</span>
                                                                                </div>
                                                                                <div className="flex items-center gap-6">
                                                                                    <div className="text-gray-600 dark:text-gray-300">
                                                                                        <span className="font-medium">Difficulty:</span> {averageAccuracy.toFixed(1)}%
                                                                                    </div>
                                                                                    <div className="text-gray-600 dark:text-gray-300">
                                                                                        <span className="font-medium">Time:</span>  {isDividedEq ? inputFields.time : totalTimeInMinutes} min
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="mt-auto p-4 bg-darkbglight flex justify-between items-center text-gray-600 dark:text-gray-200">
                                                        <button
                                                            className="mt-3  w-full justify-center rounded-md bg-indigo-600 hover:bg-indigo-500 px-3 py-2 text-sm font-semibold shadow-sm  ring-gray-300 sm:mt-0 sm:w-auto h-9 flex flex-row items-center gap-2 text-white"
                                                            onClick={() => {
                                                                setPageNumber(0)
                                                            }}
                                                        >
                                                            Prev
                                                            <ArrowUturnLeftIcon height={15} />
                                                        </button>

                                                        <div className="flex gap-4">
                                                            <button
                                                                className={`px-4 py-2 ${!selectedAssessments.some(a => a === true) ? "border-gray-500 border cursor-not-allowed" : "bg-indigo-600 hover:bg-indigo-500"} rounded-md px-4 h-9 py-2 text-sm font-semibold shadow-sm focus:outline-none text-white cursor-pointer 
                                                                 `}
                                                                onClick={() => setPageNumber(1)}
                                                                disabled={!selectedAssessments.some(a => a === true)}
                                                            >
                                                                Next
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>}
                                    </div>
                                </div>
                                {loading && <Loading />}
                            </Dialog.Panel>

                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root >
    );
}